import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthGuard } from '@/components/AuthGuard';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

// Import new components
import {
  WorkspaceHeader,
  WorkspaceStatsCards,
  WorkspaceAnalysisContent
} from '@/modules/app/components/workspace/detail';


// Import the business logic hook
import { useWorkspaceDetailLogic } from '@/modules/app/hooks/useWorkspaceAnalysisLogic';


interface WorkspaceDetailPageProps {
  workspaceId?: string; // For direct usage without routing
}

const WorkspaceDetailPage: React.FC<WorkspaceDetailPageProps> = ({ workspaceId: propWorkspaceId }) => {
  const { workspaceId: routeWorkspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  
  const workspaceId = propWorkspaceId || routeWorkspaceId;
  
  // Use the centralized business logic hook
  const {
    // State
    workspace,
    keywordData,
    allKeywordData,
    isLoading,
    error,
    searchFilter,
    
    // AI Analysis
    isAiAnalysisLoading,
    aiAnalysisError,
    getAiErrorMessage,
    clearAiError,
    
    // Computed values
    prepareBulkExportData,
    hasDataForExport,
    
    // Handlers
    handleWorkspaceNameUpdate,
    handleWorkspaceDelete,
    handleWorkspaceAiAnalysis,
    handleSearchFilterChange,
  } = useWorkspaceDetailLogic(workspaceId);





  if (isLoading) {
    return (
      <AuthGuard>
        <div className="w-full">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading workspace...</span>
          </div>
        </div>
      </AuthGuard>
    );
  }

  if (error || !workspace) {
    return (
      <AuthGuard>
        <div className="w-full">
          <div className="bg-destructive/5">
            <div className="p-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {error || 'Workspace not found'}
                </span>
              </div>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/app/workspace')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Workspaces
              </Button>
            </div>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="w-full min-h-screen">
        {/* Header Section */}
        <WorkspaceHeader
          workspace={workspace}
          workspaceId={workspaceId!}
          userId={workspace?.user_id || ''}
          bulkExportData={prepareBulkExportData()}
          hasDataForExport={hasDataForExport}
          onWorkspaceNameUpdate={handleWorkspaceNameUpdate}
          onWorkspaceDelete={handleWorkspaceDelete}
        />

        {/* Stats Cards Section */}
        <WorkspaceStatsCards
          allKeywordData={allKeywordData}
          isLoading={isLoading}
        />


        {/* Main Content Section */}
        <WorkspaceAnalysisContent
          allKeywordData={allKeywordData}
          keywordData={keywordData}
          searchFilter={searchFilter}
          isLoading={isLoading}
          workspaceName={workspace.name}
          workspaceId={workspaceId!}
          userId={workspace?.user_id || ''}
          isAiAnalysisLoading={isAiAnalysisLoading}
          aiAnalysisError={aiAnalysisError}
          onSearchFilterChange={handleSearchFilterChange}
          onAiAnalysis={handleWorkspaceAiAnalysis}
          getAiErrorMessage={getAiErrorMessage}
          clearAiError={clearAiError}
        />
      </div>
    </AuthGuard>
  );
};

export default WorkspaceDetailPage;